import { useMemo } from 'react'
import { Bar, BarChart, CartesianGrid, LabelList, XAxis, YAxis } from 'recharts'

import type { ChartConfig } from '@/components/ui/chart'
import { ChartContainer } from '@/components/ui/chart'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

interface CategoryScore {
  category: string
  managerScore: number
  directReportScore: number
}

interface CategoryScoresProps {
  data: Array<CategoryScore>
}

export function CategoryScores({ data }: CategoryScoresProps) {
  // Define the desired category order
  const categoryOrder = ['Talent', 'Culture', 'Results', 'Craft', 'Direction']

  // Transform data for the chart
  const chartData = useMemo(() => {
    return data
      .map((item) => ({
        category: item.category,
        score: (item.managerScore + item.directReportScore) / 2,
      }))
      .sort((a, b) => {
        const indexA = categoryOrder.indexOf(a.category)
        const indexB = categoryOrder.indexOf(b.category)
        return indexA - indexB
      })
  }, [data])

  // Chart configuration
  const chartConfig = {
    score: {
      label: 'Score',
      color: 'var(--chart-3)',
    },
    label: {
      color: 'var(--background)',
    },
  } satisfies ChartConfig

  return (
    <Card className="h-full p-6 bg-muted/30 rounded-lg border-none shadow-none flex flex-col">
      <CardHeader className="p-0 mb-4">
        <CardTitle className="text-lg font-semibold">Summary Average</CardTitle>
      </CardHeader>
      <CardContent className="flex items-center justify-center p-0 flex-1">
        <ChartContainer config={chartConfig} className="w-full h-full">
          <BarChart accessibilityLayer data={chartData} layout="vertical">
            <CartesianGrid horizontal={false} strokeDasharray="3 3" />
            <YAxis
              dataKey="category"
              type="category"
              tickLine={false}
              tickMargin={10}
              axisLine={false}
              hide
            />
            <XAxis
              dataKey="score"
              type="number"
              domain={[0, 100]}
              ticks={[0, 25, 50, 75, 100]}
              tickLine={false}
              axisLine={false}
              tick={{ fill: 'var(--muted-foreground)', fontSize: 12 }}
            />
            <Bar
              dataKey="score"
              fill="var(--color-score)"
              radius={4}
              animationDuration={1000}
              barSize={40}
              fillOpacity={1}
            >
              <LabelList
                dataKey="category"
                position="insideLeft"
                offset={8}
                className="fill-foreground font-sm"
                fontSize={16}
              />
              <LabelList
                dataKey="score"
                position="insideRight"
                offset={8}
                className="fill-foreground font-sm"
                fontSize={16}
                formatter={(value) =>
                  typeof value === 'number' ? `${value.toFixed(1)}%` : value
                }
              />
            </Bar>
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}
